/**
 * Прогретое: то, что уже скачано и готово появиться мгновенно.
 *
 * ЗАЧЕМ ВООБЩЕ. Слой, выведенный в кадр, обязан появиться СРАЗУ:
 * пауза в двести миллисекунд — это заметная задержка в эфире,
 * а звук саундборда с такой задержкой опаздывает к реакции, ради
 * которой его и нажали (docs/04).
 *
 * ПОЧЕМУ НЕ «ПРОСТО ДЕРЖАТЬ ВСЁ». Оверлей работает часами на машине
 * стримера — рядом с игрой, кодировщиком и браузером. Прогрев
 * без предела съел бы память, которой там и так впритык, и заметил
 * бы это стример по просевшим кадрам, а не по нашему логу. Отсюда
 * бюджет 150 МБ из docs/04 и вытеснение по LRU.
 *
 * ПОЧЕМУ LRU, А НЕ «ПО ЧАСТОТЕ». Частота говорит, что грузить
 * ПЕРВЫМ (этот порядок приходит с сервера — он видит `audit_log`),
 * а вытеснять надо то, к чему давно не обращались: звук, который
 * жали сто раз на прошлой неделе и ни разу сегодня, держать в памяти
 * незачем.
 */
/** Бюджет прогрева, байт. 150 МБ из docs/04. */
export const WARM_BUDGET_BYTES = 150 * 1024 * 1024;
/**
 * Кеш прогретого с бюджетом и вытеснением по давности.
 *
 * Порядок держится самим `Map`: он перебирается в порядке вставки,
 * поэтому «самый давний» — это первый ключ, а обращение переставляет
 * запись в конец. Отдельный список давности был бы вторым источником
 * правды о том же самом.
 */
export class WarmCache {
    entries = new Map();
    budget;
    onEvict;
    used = 0;
    constructor(options = {}) {
        this.budget = options.budgetBytes ?? WARM_BUDGET_BYTES;
        this.onEvict = options.onEvict;
    }
    /** Сколько байт занято. */
    get bytes() {
        return this.used;
    }
    /** Сколько записей лежит. */
    get size() {
        return this.entries.size;
    }
    /** Предел, за который не выходим. */
    get budgetBytes() {
        return this.budget;
    }
    /**
     * Есть ли запись — БЕЗ отметки об использовании.
     *
     * Проверка наличия использованием не является: считай мы её
     * обращением, повторный прогрев переставлял бы записи в конец
     * очереди, и вытесняться начало бы не то, что залежалось,
     * а то, о чём давно не спрашивали.
     */
    has(key) {
        return this.entries.has(key);
    }
    /** Достаёт запись и отмечает её использованной. */
    get(key) {
        const entry = this.entries.get(key);
        if (entry === undefined)
            return null;
        // Переставляем в конец: `Map` перебирается в порядке вставки,
        // и удаление с повторной вставкой — это и есть «стало самым
        // свежим»
        this.entries.delete(key);
        this.entries.set(key, entry);
        return entry.value;
    }
    /**
     * Кладёт прогретое.
     *
     * Возвращает false, если запись НЕ ВЛЕЗАЕТ В БЮДЖЕТ ЦЕЛИКОМ.
     * Не «освободим место любой ценой»: один ролик в двести мегабайт
     * иначе выбросил бы весь прогрев и сам бы не поместился —
     * в результате пусто и там и там.
     */
    put(key, value, bytes) {
        const weight = Number.isFinite(bytes) && bytes > 0 ? Math.ceil(bytes) : 0;
        if (weight > this.budget)
            return false;
        // Замена той же записи не должна считаться дважды
        this.drop(key, false);
        this.entries.set(key, { value, bytes: weight });
        this.used += weight;
        this.evict();
        return true;
    }
    /** Убирает запись, сообщая о вытеснении. */
    delete(key) {
        this.drop(key, true);
    }
    /** Убирает всё. Нужно при смене канала и в тестах. */
    clear() {
        for (const key of [...this.entries.keys()]) {
            this.drop(key, true);
        }
    }
    /**
     * Ключи от самого давнего к самому свежему.
     *
     * Нужен тестам и отладке: порядок вытеснения — это то, что здесь
     * важнее всего, и проверять его косвенно значило бы гадать.
     */
    keys() {
        return [...this.entries.keys()];
    }
    drop(key, notify) {
        const entry = this.entries.get(key);
        if (entry === undefined)
            return;
        this.entries.delete(key);
        this.used -= entry.bytes;
        if (notify) {
            this.onEvict?.(key, entry.value);
        }
    }
    evict() {
        for (const key of this.entries.keys()) {
            if (this.used <= this.budget)
                return;
            // Первый ключ перебора — самый давний по обращению
            this.drop(key, true);
        }
    }
}
/**
 * Общий кеш страницы.
 *
 * Модульная переменная по той же причине, что и звуковой граф:
 * прогрев нужен и виджетам (картинка припаркованного слоя),
 * и оверлею (звуки саундборда), а тащить его в контракт виджета
 * значило бы объяснять кеш каждому следующему примитиву.
 *
 * ОДИН НА СТРАНИЦУ — это требование, а не удобство: два кеша
 * означали бы два бюджета по 150 МБ, то есть отсутствие бюджета.
 */
let current = null;
export function setWarmCache(cache) {
    current = cache;
}
export function warmCache() {
    return current;
}
/**
 * Во сколько байт обходится картинка после декодирования.
 *
 * Считается по площади, а не по весу файла: в памяти лежит
 * растр, и сжатый до тридцати килобайт PNG размером 4000×4000
 * занимает шестьдесят четыре мегабайта. Судить о прогреве по весу
 * файла значило бы промахнуться в тысячу раз в опасную сторону.
 */
export function imageBytes(width, height) {
    if (!Number.isFinite(width) || !Number.isFinite(height))
        return 0;
    return Math.max(0, Math.ceil(width)) * Math.max(0, Math.ceil(height)) * 4;
}
/**
 * Во сколько байт обходится декодированный звук.
 *
 * Web Audio держит его как float32 на канал: минута стерео —
 * это двадцать мегабайт, и без учёта пары длинных подложек
 * бюджет кончился бы незаметно.
 */
export function bufferBytes(buffer) {
    return buffer.length * buffer.numberOfChannels * 4;
}
//# sourceMappingURL=warm.js.map